import { isAxiosError } from "axios";
import { format } from "date-fns";
import { Button, ScrollView } from "devextreme-react";
import notify from "../../lib/notify";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getAcceptanceStatus } from "../../api/acceptedWorkingHours";
import { AbsenceToConfirm, getAnnualLeaveLimit } from "../../api/calendarData";
import {
  fetchNotifications,
  markNotificationAsRead,
} from "../../api/notifications";
import { useAuth } from "../../contexts/auth";
import axios from "../../lib/axios";
import { Announcement, Paginated, RoleEnum } from "../../types";
import { MyPresence } from "../presence/components/MyPresence";
import { ViewSwitchProvider } from "../presence/contexts/ViewSwitchContext";
import { AcceptWorkingHoursDialog } from "./components/AcceptWorkingHoursDialog";
import "./Home.scss";

const CalendarIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ClockIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);

const BellIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
  </svg>
);

const MegaphoneIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 11l18-7v18l-18-7v-4z" />
    <path d="M7 15v3a2 2 0 0 0 2 2h1" />
  </svg>
);

const WarningIcon = () => (
  <svg
    className="home-alert-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const SkeletonLines = ({ rows = 2 }: { rows?: number }) => (
  <div className="home-skeleton" aria-hidden="true">
    {Array.from({ length: rows }).map((_, index) => (
      <div key={index} className="home-skeleton-row">
        <div className="home-skeleton-line" style={{ width: "35%" }} />
        <div className="home-skeleton-line" style={{ width: "90%" }} />
        <div className="home-skeleton-line" style={{ width: "65%" }} />
      </div>
    ))}
  </div>
);

export const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const { user } = useAuth();
  const role = user?.roles;
  const isNotEmployee = role && !role.includes(RoleEnum.Pracownik);

  const isTimeManager = user?.isTimeManager;
  const [currentMonth] = useState<Date>(new Date());
  const { i18n } = useTranslation();
  const [notification, setNotification] = useState<
    {
      id: number;
      dateCreated: Date;
      message: string;
      title: string;
      markAsReadButtonDisabled: boolean;
    }[]
  >([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [leavingNotificationIds, setLeavingNotificationIds] = useState<
    number[]
  >([]);
  const [absences, setAbsences] = useState<AbsenceToConfirm[]>([]);
  const isManager = role && role.includes(RoleEnum.Kierownik);
  const isNotForeman = role && !role.includes(RoleEnum.Majster);

  const [workingHoursToAccept, setWorkingHoursToAccept] = useState(false);
  const [acceptDialogVisible, setAcceptDialogVisible] = useState(false);

  const [limitsLoading, setLimitsLoading] = useState(false);
  const [remainingAnnualLeave, setRemainingAnnualLeave] = useState({
    days: 0,
    hours: 0,
  });

  const [unconfirmedAnnualLeave, setUnconfirmedAnnualLeave] = useState({
    days: 0,
    hours: 0,
  });

  type AbsenceToConfirm = {
    id: number;
    employeeId: number;
    companyId: number;
    from: string;
    to: string;
    name: string;
    note: string;
    firstName: string;
    lastName: string;
  };

  const checkWorkingHoursAcceptance = useCallback(async () => {
    // Poza oknem 1-7 dnia miesiąca backend zwraca accepted: null - wtedy nie pokazujemy alertu.
    // Alert pojawia się tylko, gdy godziny nie zostały jeszcze zatwierdzone (accepted === false).
    const response = await getAcceptanceStatus();
    if (response.isOk) {
      setWorkingHoursToAccept(response.data.accepted === false);
    }
  }, []);

  const fetchDataForConfirmAbsence = useCallback(async () => {
    try {
      const response = await AbsenceToConfirm(user?.companyId);

      if (response.isOk && response.data) {
        const fetchedAbsences: AbsenceToConfirm[] = response.data;

        setAbsences(fetchedAbsences);

        return fetchedAbsences;
      } else {
        notify(
          `${t("Error:")} ${response.error || t("Unknown error")}`,
          "error",
        );

        return [];
      }
    } catch (error) {
      notify(`${t("Error:")} ${error || t("Unknown error")}`, "error");
      return [];
    }
  }, [t, user?.companyId]);

  const fetchHolidayLimit = useCallback(
    async (year: number, month: number) => {
      setLimitsLoading(true);
      try {
        const data = await getAnnualLeaveLimit(year, month);

        setRemainingAnnualLeave({
          days: data.remainingAnnualLeaveDays,
          hours: data.remainingAnnualLeaveHours,
        });

        setUnconfirmedAnnualLeave({
          days: data.unconfirmedAnnualLeaveDays,
          hours: data.unconfirmedAnnualLeaveHours,
        });
      } catch (error) {
        notify(
          error instanceof Error
            ? error.message
            : t("common.unknownErrorOccurred"),
        );
      } finally {
        setLimitsLoading(false);
      }
    },
    [t],
  );

  const downloadNotifications = useCallback(async () => {
    setNotificationsLoading(true);
    try {
      const response = await fetchNotifications(i18n.language);

      if (response.isOk) {
        setNotification(
          response.data.map((notification) => ({
            ...notification,
            dateCreated: new Date(notification.dateCreated),
            markAsReadButtonDisabled: false,
          })),
        );
      } else {
        notify(
          `${t("Error:")} ${response.error || t("Unknown error")}`,
          "error",
        );
      }
    } catch (error) {
      notify(`${t("Error:")} ${error || t("Unknown error")}`, "error");
    } finally {
      setNotificationsLoading(false);
    }
  }, [i18n.language, t]);

  const fetchAnnouncements = useCallback(() => {
    setAnnouncementsLoading(true);
    axios
      .get<Paginated<Announcement>>("/api/announcements", {
        params: {
          pageNumber: 1,
          pageSize: 3,
        },
      })
      .then(({ data: { items } }) => setAnnouncements(items))
      .catch((error) => {
        let message = t("common.unknownErrorOccurred");

        if (isAxiosError(error)) {
          if (error.response) {
            if (error.response.status === 403) {
              message = t("noPermissions");
            } else if (
              error.response.data &&
              "message" in error.response.data
            ) {
              message = error.response.data.message;
            }
          } else if (error.request) {
            message = t("common.noResponseFromServer");
          }
        }

        notify(message, "error", 5000);
      })
      .finally(() => setAnnouncementsLoading(false));
  }, [t]);

  const handleMarkNotificationAsReadButtonClick = (notificationId: number) => {
    setNotification((notifications) =>
      notifications.map((notification) => {
        if (notification.id === notificationId) {
          return { ...notification, markAsReadButtonDisabled: true };
        }

        return notification;
      }),
    );

    markNotificationAsRead(notificationId)
      .then(() => {
        setLeavingNotificationIds((ids) => [...ids, notificationId]);

        setTimeout(() => {
          setNotification((notifications) =>
            notifications.filter(
              (notification) => notification.id !== notificationId,
            ),
          );
          setLeavingNotificationIds((ids) =>
            ids.filter((id) => id !== notificationId),
          );
        }, 280);
      })
      .catch((error) => {
        notify(error, "error");

        setNotification((notifications) =>
          notifications.map((notification) => {
            if (notification.id === notificationId) {
              return { ...notification, markAsReadButtonDisabled: false };
            }

            return notification;
          }),
        );
      });
  };

  useEffect(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    fetchHolidayLimit(year, month);
    downloadNotifications();
    fetchAnnouncements();
    if ((isNotEmployee && isNotForeman) || isTimeManager) {
      fetchDataForConfirmAbsence();
    }
    checkWorkingHoursAcceptance();
  }, [
    currentMonth,
    fetchHolidayLimit,
    downloadNotifications,
    fetchAnnouncements,
    fetchDataForConfirmAbsence,
    checkWorkingHoursAcceptance,
    isNotEmployee,
    isNotForeman,
    isTimeManager,
  ]);

  return (
    <ScrollView useNative>
      <div className="p-6">
        <div className="mb-2 text-2xl font-bold leading-none">
          {t("home.header")}
        </div>
        <div className="mb-4 text-base">{t("home.headerTekst")}</div>
        {workingHoursToAccept ? (
          <div className="home-alert mb-4 p-4 rounded-md bg-yellow-100 text-yellow-900 dark:bg-yellow-200 dark:text-black flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <WarningIcon />
              <span>{t("acceptWorkingHours.alert")}</span>
            </div>
            <Button
              text={t("acceptWorkingHours.openDialog")}
              type="default"
              stylingMode="contained"
              onClick={() => setAcceptDialogVisible(true)}
            />
          </div>
        ) : null}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="flex flex-col gap-4">
            <div className="home-card p-4 border border-solid border-[#E0E0E0] dark:border-[#616161] rounded-md bg-white dark:bg-[#292929] flex flex-col justify-between">
              <div>
                <div className="mb-4 text-lg font-bold">
                  {t("home.yourVacationLeave")}
                </div>
                {limitsLoading ? (
                  <SkeletonLines rows={1} />
                ) : (
                  <div className="home-stats">
                    <div className="home-stat">
                      <div className="home-stat-icon">
                        <CalendarIcon />
                      </div>
                      <div className="min-w-0">
                        <div className="home-stat-label">
                          {t("home.holidayLimit")}
                        </div>
                        <div className="home-stat-value">
                          {t("holiday.daysHours", remainingAnnualLeave)}
                        </div>
                      </div>
                    </div>
                    <div className="home-stat">
                      <div className="home-stat-icon">
                        <ClockIcon />
                      </div>
                      <div className="min-w-0">
                        <div className="home-stat-label">
                          {t("home.UnconfirmedHoliday")}
                        </div>
                        <div className="home-stat-value">
                          {t("holiday.daysHours", unconfirmedAnnualLeave)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {isManager && absences.length > 0 ? (
                <div className="mt-4 p-3 rounded-md bg-yellow-100 text-yellow-900 dark:bg-yellow-200 dark:text-black">
                  <span className="font-semibold">
                    {`${t("home.information")} `}
                  </span>
                  <span>{t("home.holidayToConfirm")}</span>
                </div>
              ) : null}
            </div>
            <div
              className="home-card border border-solid border-[#E0E0E0] dark:border-[#616161] rounded-md bg-white dark:bg-[#292929]"
              style={{ animationDelay: "60ms" }}
            >
              <div className="px-4 py-3 flex items-center gap-2 text-lg font-bold border border-l-0 border-t-0 border-r-0 border-solid border-b-[#E0E0E0] dark:border-b-[#616161]">
                <span>{t("home.notifications.header")}</span>
                <span
                  className={
                    notification.length > 0
                      ? "home-badge"
                      : "home-badge home-badge-muted"
                  }
                >
                  {notification.length}
                </span>
              </div>
              <div className="max-h-96 overflow-y-auto p-4">
                {notificationsLoading ? (
                  <SkeletonLines rows={2} />
                ) : notification.length === 0 ? (
                  <div className="home-empty">
                    <BellIcon />
                    <div>{t("home.notifications.empty")}</div>
                  </div>
                ) : (
                  <div className="flex flex-col">
                    {notification.map((notification, index, array) => (
                      <div
                        key={notification.id}
                        className={
                          leavingNotificationIds.includes(notification.id)
                            ? "home-notification home-notification-leaving"
                            : "home-notification"
                        }
                      >
                        <div className="mb-2 flex-grow">
                          <div className="mb-2 flex justify-between items-center gap-2">
                            <div className="font-bold">
                              {notification.title}
                            </div>
                            <div>
                              {format(
                                notification.dateCreated,
                                "dd.MM.yyyy, HH:mm:ss",
                              )}
                            </div>
                          </div>
                          <div>{notification.message}</div>
                        </div>
                        <Button
                          stylingMode="contained"
                          type="default"
                          text={
                            notification.markAsReadButtonDisabled
                              ? t("common.sending")
                              : t("home.notifications.markAsRead")
                          }
                          icon="check"
                          disabled={notification.markAsReadButtonDisabled}
                          onClick={() =>
                            handleMarkNotificationAsReadButtonClick(
                              notification.id,
                            )
                          }
                        />
                        {array.length - 1 !== index ? (
                          <hr className="my-4 h-px bg-gray-200 border-0 dark:bg-gray-700" />
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div
              id="announcements"
              className="home-card border border-solid border-[#E0E0E0] dark:border-[#616161] rounded-md bg-white dark:bg-[#292929] max-h-[500px] overflow-y-auto"
              style={{ animationDelay: "120ms" }}
            >
              <div className="px-4 py-3 flex justify-between items-center text-lg font-bold border border-l-0 border-t-0 border-r-0 border-solid border-b-[#E0E0E0] dark:border-b-[#616161]">
                <span>{t("home.announcements")}</span>
                <Button
                  stylingMode="outlined"
                  text={t("home.gotoAnnouncements")}
                  onClick={() => navigate("announcements")}
                />
              </div>
              <div className="max-h-96 overflow-y-auto p-4">
                {announcementsLoading ? (
                  <SkeletonLines rows={2} />
                ) : announcements.length === 0 ? (
                  <div className="home-empty">
                    <MegaphoneIcon />
                    <div>{t("announcements.list.messages.empty")}</div>
                  </div>
                ) : (
                  <>
                    {announcements.map((announcement, index, array) => (
                      <div key={announcement.id}>
                        <div>
                          <div className="mb-1 font-bold">
                            {announcement.title}
                          </div>
                          <div
                            className="mb-2"
                            dangerouslySetInnerHTML={{
                              __html: announcement.content,
                            }}
                          />
                          {announcement.files?.length > 0 && (
                            <div className="mt-2 flex flex-col gap-2">
                              {announcement.files.map((file) => (
                                <div key={file.id}>
                                  {file.fileName.match(
                                    /\.(jpg|jpeg|png|gif|webp)$/i,
                                  ) ? (
                                    <img
                                      src={file.url}
                                      alt={file.fileName}
                                      className="max-w-full rounded-md border border-gray-300"
                                    />
                                  ) : (
                                    <a
                                      href={file.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 underline"
                                    >
                                      {file.fileName}
                                    </a>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="text-xs mb-1">
                            {announcement.roles?.length > 0 && (
                              <div>
                                {t(
                                  "announcements.list.messages.addressedToRoles",
                                )}
                                :{" "}
                                {announcement.roles
                                  .map((r) => r.name)
                                  .join(", ")}
                              </div>
                            )}
                            {announcement.projects?.length > 0 && (
                              <div>
                                {t(
                                  "announcements.list.messages.addressedToProjects",
                                )}
                                :{" "}
                                {announcement.projects
                                  .map((p) => p.acronym)
                                  .join(", ")}
                              </div>
                            )}
                            {announcement.roles?.length === 0 &&
                              announcement.projects?.length === 0 &&
                              t("announcements.list.messages.addressedToAll")}
                          </div>

                          <div className="flex gap-1">
                            <div className="text-xs">
                              {t("announcements.list.messages.createdBy", {
                                user: announcement.createdBy,
                                date: announcement.dateCreated
                                  ? format(
                                      new Date(announcement.dateCreated),
                                      "dd.MM.yyyy HH:mm",
                                    )
                                  : "",
                              })}
                            </div>

                            {announcement.updatedBy &&
                              announcement.dateModified && (
                                <div className="text-xs">
                                  {t("announcements.list.messages.updatedBy", {
                                    user: announcement.updatedBy,
                                    date: format(
                                      new Date(announcement.dateModified),
                                      "dd.MM.yyyy HH:mm",
                                    ),
                                  })}
                                </div>
                              )}
                          </div>
                        </div>

                        {array.length - 1 !== index ? (
                          <hr className="my-3 h-px bg-gray-200 border-0 dark:bg-gray-700" />
                        ) : null}
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
          <div
            className="home-card p-4 border border-solid border-[#E0E0E0] dark:border-[#616161] rounded-md bg-white dark:bg-[#292929] flex flex-col"
            style={{ animationDelay: "90ms" }}
          >
            <div className="mb-4 text-lg font-bold">{t("home.calendar")}</div>
            <ViewSwitchProvider>
              <MyPresence embedded />
            </ViewSwitchProvider>
          </div>
        </div>
      </div>
      <AcceptWorkingHoursDialog
        visible={acceptDialogVisible}
        onHiding={() => setAcceptDialogVisible(false)}
        onAccepted={() => {
          setAcceptDialogVisible(false);
          setWorkingHoursToAccept(false);
        }}
      />
    </ScrollView>
  );
};
